import { reatomAsync } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import { nanoid } from 'nanoid'

import { Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import { fetchQuoteRequestAction } from '@root/entities/quote/atoms'
import { joinedInviteCodeAtom } from '@root/entities/referral/atoms'
import { Error } from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { SwapRequestSnackbar } from '../ui/desktop/swap-request-snackbar'

import { processSwapAction } from './reatom-swap'
import { activeSwapsAtom } from './reatom-swap-requests'

import type { ProcessSwapArgs } from './swap-requests'
import type { SwapRequest } from '../model/swap-request-model'
import type { TypeHelper } from '@blade/std-lib'
import type { Quote } from '@root/entities/quote'

type Amount =
  | {
      amount: BigNumber | 'full'
    }
  | {
      /**
       * From 0 to 1
       */
      percentage: number
    }

export type SellSwapArgs = TypeHelper.OmitKey<
  SwapRequest,
  'id' | 'type' | 'amount'
> & {
  quote: Quote.Quote | undefined
} & Amount

export const processSellSwapAction = reatomAsync(
  async (ctx, request: SellSwapArgs) => {
    const {
      pair,
      wallet,
      settings,
      quote: quote_,
      baseCurrencyOverride,
    } = request
    const asset = ctx.get(wallet.assetsByCurrency)[pair.quote.address]
    const balance = asset ? ctx.get(asset.balance) : BigNumber(0)

    const currencyIn = pair.quote
    const currencyOut = baseCurrencyOverride ?? pair.base

    const amount = (() => {
      if ('amount' in request) {
        if (request.amount === 'full') {
          return balance
        }
        return request.amount
      }
      return balance.multipliedBy(request.percentage)
    })()

    const model = activeSwapsAtom.create(ctx, {
      pair: pair,
      wallet: wallet,
      settings: settings,
      quote: quote_,
      type: 'sell',
      id: nanoid(),
      amount: amount,
    })
    showSnackbarAction(ctx, {
      delay: 'permanent',
      onClose: () => {
        activeSwapsAtom.delete(ctx, model.request.id)
      },
      element: <SwapRequestSnackbar model={model} />,
    })

    const quoteResult = await ctx.schedule(() => {
      if (quote_ && quote_.inputAmount.eq(amount)) {
        return Result.succeed(quote_)
      }

      return fetchQuoteRequestAction(ctx, {
        amount: amount,
        currencyIn,
        currencyOut,
        network: pair.network,
        poolAddress: pair.address,
        providerId: pair.provider.id,
        type: 'EXACT_INPUT',
        wallet: wallet,
        inviteCode: ctx.get(joinedInviteCodeAtom),
        settings,
      })
    })

    if (Result.isFailure(quoteResult)) {
      const error = Error.UnknownError.fromUnknown('Quote error')
      model.handleError(ctx, error)
      return Result.fail(error)
    }

    const quote = quoteResult.success
    model.handleQuote(ctx, quote)
    const accounts = ctx.get(pair.accounts)
    const pairQuoteTokenProgram = ctx.get(pair.quoteTokenProgram)!

    const amountRaw = CurrencyModel.convertAmountToBigInt(currencyIn)(amount)

    const args: ProcessSwapArgs = {
      id: nanoid(),
      amount: amount,
      rawAmountIn: amountRaw.toString(),
      inAddress: currencyIn.address,
      outAddress: currencyOut.address,
      network: pair.network,
      pairAddress: pair.address,
      provider: pair.provider,
      pairQuoteCurrency: currencyOut.address,
      swapSettings: settings,
      walletId: wallet.id,
      walletAddress: wallet.address,
      pairQuoteTokenProgram,
      type: 'sell',
      quote,
      accounts,
    }

    const result = await ctx.schedule(() => processSwapAction(ctx, args))

    if (Result.isFailure(result)) {
      model.handleError(ctx, result.failure)
      return result
    }
    model.handleSendSwapData(ctx, result.success)

    return result
  },
  'processSellSwapAction',
)
